<script setup lang="ts">
// Strategy -> Edge (2026-10-31, paolo): the edge map ("where each fund's return comes from", GET
// /api/risk/edge-map), moved here from Risk -> Live vs backtest (FOF). Selecting a fund on the map opens
// its source-of-edge stack underneath (in Risk the same click switched the parity view to that fund).
// The 120/250-day window is shared with Risk's per-fund stack (same localStorage key).
import { ref, onMounted } from 'vue'
import { api, type EdgeMapResponse } from '../../api/client'
import EdgeMap from '../edge/EdgeMap.vue'
import EdgeSourceStack from '../edge/EdgeSourceStack.vue'

const EDGE_WINDOW_KEY = 'sw.risk.edge.window'
function readEdgeWindow(): 120 | 250 {
  try { return localStorage.getItem(EDGE_WINDOW_KEY) === '120' ? 120 : 250 } catch { return 250 }
}
const edgeWindow = ref<120 | 250>(readEdgeWindow())
const edgeMap = ref<EdgeMapResponse | null>(null)
const edgeLoading = ref(false)
const edgeError = ref<string | null>(null)
const selectedFund = ref<string | null>(null)
let edgeSeq = 0

async function loadEdgeMap() {
  const seq = ++edgeSeq
  edgeLoading.value = true
  try {
    const d = await api.edgeMap(edgeWindow.value)
    if (seq === edgeSeq) { edgeMap.value = d; edgeError.value = null }
  } catch (e) {
    if (seq === edgeSeq) { edgeMap.value = null; edgeError.value = e instanceof Error ? e.message : String(e) }
  } finally {
    if (seq === edgeSeq) edgeLoading.value = false
  }
}

function setEdgeWindow(w: 120 | 250) {
  // the same window again is a retry when its last load failed
  if (w === edgeWindow.value && !edgeError.value) return
  edgeWindow.value = w
  try { localStorage.setItem(EDGE_WINDOW_KEY, String(w)) } catch { /* storage unavailable */ }
  void loadEdgeMap()
}

function selectFund(id: string) {
  selectedFund.value = selectedFund.value === id ? null : id
}

onMounted(() => { void loadEdgeMap() })
</script>

<template>
  <div class="edge-panel" data-testid="edge-panel">
    <EdgeMap :data="edgeMap" :loading="edgeLoading" :error="edgeError" :window="edgeWindow"
             @select="selectFund" @window="setEdgeWindow" />
    <div v-if="selectedFund" class="edge-fund" data-testid="edge-fund-detail">
      <button type="button" class="edge-fund-close" data-testid="edge-fund-close" @click="selectedFund = null">Close {{ selectedFund }}</button>
      <EdgeSourceStack :key="selectedFund" :fund-id="selectedFund" :window="edgeWindow" @window="setEdgeWindow" />
    </div>
  </div>
</template>

<style scoped>
.edge-panel { display: flex; flex-direction: column; gap: 1rem; }
.edge-fund { display: flex; flex-direction: column; gap: 0.5rem; }
.edge-fund-close { align-self: flex-end; min-height: 44px; padding: 0 1rem; background: none; color: #8b949e; border: 1px solid #30363d; border-radius: 6px; cursor: pointer; }
.edge-fund-close:hover { color: #c9d1d9; }
</style>
